import { OP, Script, Utils } from '@bsv/sdk';

/**
 * Codec for Pelf's on-chain personal-best records.
 *
 * A record is a single 0-satoshi `OP_FALSE OP_RETURN <prefix> <json>` output.
 * OP_FALSE first makes the output provably unspendable, so wallets and miners
 * treat it as data rather than a UTXO worth indexing.
 *
 * This module is deliberately free of wallet/DB imports so both the browser
 * (which builds and signs the output) and the API route (which fetches the
 * broadcast transaction back and re-reads it) share one definition. If the two
 * sides ever disagree about the byte layout, a record would verify as tampered.
 */

export const PELF_SCORE_PREFIX = 'PELFSCORE';
export const PELF_SCORE_VERSION = 1;

/**
 * On-chain payload. Field names are short because every byte is a mining fee,
 * and `pk` is included so a record is self-describing - anyone reading the
 * chain can tell whose best it is without our database.
 */
export type OnChainScorePayload = {
  /** Payload schema version */
  v: number;
  /** Identity public key of the player (compressed hex) */
  pk: string;
  /** Run score */
  s: number;
  /** Hero type id the run was played with */
  h: string;
  /** Deepest level index reached (0-based) */
  l: number;
  /** Unix ms at which the client built the record */
  t: number;
};

const MAX_SCORE = 10_000_000;
const MAX_LEVEL = 999;
const PUBKEY_RE = /^0[23][0-9a-fA-F]{64}$/;
const HERO_RE = /^[a-z0-9-]{1,32}$/;

/** Throwing validator shared by the builder and the reader. */
export function assertValidScorePayload(p: OnChainScorePayload): void {
  if (p.v !== PELF_SCORE_VERSION) throw new Error(`Unsupported payload version: ${p.v}`);
  if (!PUBKEY_RE.test(p.pk)) throw new Error('Invalid identity key in payload');
  if (!HERO_RE.test(p.h)) throw new Error('Invalid hero id in payload');
  if (!Number.isInteger(p.s) || p.s < 0 || p.s > MAX_SCORE) throw new Error('Invalid score');
  if (!Number.isInteger(p.l) || p.l < 0 || p.l > MAX_LEVEL) throw new Error('Invalid level');
  if (!Number.isInteger(p.t) || p.t <= 0) throw new Error('Invalid timestamp');
}

export function buildScorePayload(args: {
  identityKey: string;
  score: number;
  heroType: string;
  levelIndex: number;
  timestamp: number;
}): OnChainScorePayload {
  const payload: OnChainScorePayload = {
    v: PELF_SCORE_VERSION,
    pk: args.identityKey,
    s: args.score,
    h: args.heroType,
    l: args.levelIndex,
    t: args.timestamp,
  };
  assertValidScorePayload(payload);
  return payload;
}

/** Locking script hex for the data output carrying `payload`. */
export function buildScoreLockingScript(payload: OnChainScorePayload): string {
  assertValidScorePayload(payload);
  const pushData = (text: string) => {
    const bytes = Utils.toArray(text, 'utf8');
    // The JSON payload is ~150 bytes (the 66-char identity key dominates), so
    // it needs OP_PUSHDATA1 rather than a direct-length opcode. readPushes()
    // understands both, plus OP_PUSHDATA2 for headroom.
    if (bytes.length > 0xff) {
      throw new Error('Score payload push exceeds OP_PUSHDATA1 range');
    }
    return { op: bytes.length <= 75 ? bytes.length : OP.OP_PUSHDATA1, data: bytes };
  };
  return new Script([
    { op: OP.OP_FALSE },
    { op: OP.OP_RETURN },
    pushData(PELF_SCORE_PREFIX),
    pushData(JSON.stringify(payload)),
  ]).toHex();
}

/**
 * Read the data pushes that follow `OP_FALSE OP_RETURN` in a locking script.
 *
 * Done by hand rather than with Script.fromHex: the SDK's parser stops at
 * OP_RETURN (everything after it is unexecuted data), so chunk-walking would
 * see no pushes at all.
 */
function readPushes(scriptHex: string): string[] | null {
  const hex = scriptHex.toLowerCase();
  // OP_FALSE OP_RETURN, or a bare OP_RETURN for tolerance
  let offset: number;
  if (hex.startsWith('006a')) offset = 4;
  else if (hex.startsWith('6a')) offset = 2;
  else return null;

  const pushes: string[] = [];
  while (offset < hex.length) {
    const opcode = parseInt(hex.slice(offset, offset + 2), 16);
    if (!Number.isFinite(opcode)) return null;
    offset += 2;
    let length: number;
    if (opcode >= 1 && opcode <= 75) {
      length = opcode;
    } else if (opcode === OP.OP_PUSHDATA1) {
      length = parseInt(hex.slice(offset, offset + 2), 16);
      offset += 2;
    } else if (opcode === OP.OP_PUSHDATA2) {
      // little-endian uint16
      const lo = parseInt(hex.slice(offset, offset + 2), 16);
      const hi = parseInt(hex.slice(offset + 2, offset + 4), 16);
      length = lo | (hi << 8);
      offset += 4;
    } else {
      // Not a pushdata - stop rather than guess
      break;
    }
    const dataHex = hex.slice(offset, offset + length * 2);
    if (dataHex.length < length * 2) return null;
    offset += length * 2;
    pushes.push(Utils.toUTF8(Utils.toArray(dataHex, 'hex')));
  }
  return pushes;
}

/** Decode a Pelf score record from one output's locking script, or null. */
export function decodeScoreFromLockingScript(scriptHex: string): OnChainScorePayload | null {
  const pushes = readPushes(scriptHex);
  if (!pushes || pushes.length < 2 || pushes[0] !== PELF_SCORE_PREFIX) return null;
  try {
    const payload = JSON.parse(pushes[1]) as OnChainScorePayload;
    assertValidScorePayload(payload);
    return payload;
  } catch {
    return null;
  }
}

/**
 * Find the first Pelf score record among a transaction's output scripts.
 * Callers pass raw locking-script hex so this stays independent of how the
 * transaction was obtained (SDK Transaction, block explorer JSON, ...).
 */
export function findScoreRecord(outputScriptHexes: string[]): OnChainScorePayload | null {
  for (const scriptHex of outputScriptHexes) {
    const payload = decodeScoreFromLockingScript(scriptHex);
    if (payload) return payload;
  }
  return null;
}
